import { useState } from "react";
import { Check, Loader2, Mail } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { fireBlueConfetti } from "@/lib/blueConfetti";
import { trackEvent } from "@/utils/mixpanel";
import mixpanel from "@/utils/mixpanel";
import { pushDataLayerEvent } from "@/utils/googleAnalytics";
import { getAttributionParams } from "@/lib/attribution";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// "Email me the Mac link" — the mobile/off-platform answer to the download CTA.
//
// A phone cannot install the dmg, and the funnel audit showed exactly what
// phones do instead: tap the macOS button repeatedly (nothing happens in the
// IG/FB in-app browser), then misuse the Windows waitlist as a "notify me"
// form. This captures that intent honestly: one email field, and the
// `mac-link-email` edge fn sends a Loops transactional with a personalized
// download link (the visitor's distinct_id + ad attribution ride the link, so
// when they open it on their Mac the download still joins their web session
// and campaign — the same stitching the direct button does).

const emailSchema = z
  .string()
  .trim()
  .email({ message: "Enter a valid email address" })
  .max(255, { message: "Email must be less than 255 characters" });

interface Props {
  /** Which surface the request came from ("hero", "pricing", "installing"). */
  placement: string;
  /** Trigger button classes (defaults match the hero download button). */
  className?: string;
  iconClassName?: string;
  /** Trigger label — the default reads right on a phone. */
  label?: string;
  style?: React.CSSProperties;
}

export default function MacLinkEmailButton({
  placement,
  className = "btn-gamey inline-flex items-center gap-2.5 bg-white hover:bg-slate-50 text-[#0B6BB0] font-bold uppercase text-sm xl:text-base tracking-wide px-8 py-4 xl:px-9 xl:py-[1.15rem] rounded-2xl transition-colors",
  iconClassName = "w-4 h-4 xl:w-5 xl:h-5",
  label = "Email me the Mac link",
  style,
}: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast({
        title: "Check your email",
        description: parsed.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    // distinct_id + attribution let the emailed link carry the same identity
    // params the direct download button stamps (downloadQueryString) — the
    // server rebuilds the URL against its own whitelist, so junk here can
    // never reach the link.
    let distinctId: string | undefined;
    try {
      const id = mixpanel.get_distinct_id();
      if (typeof id === "string" && id.length >= 8) distinctId = id;
    } catch {
      /* identity optional */
    }
    const { data, error } = await supabase.functions.invoke("mac-link-email", {
      body: {
        email: parsed.data.toLowerCase(),
        placement,
        distinct_id: distinctId,
        attribution: getAttributionParams(),
      },
    });
    setSubmitting(false);
    if (error || (data && data.ok === false)) {
      toast({
        title: "Something went wrong",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }
    try {
      trackEvent("mac_link_requested", { placement });
      // Its OWN GTM event — deliberately NOT email_captured (that one maps to
      // the Google Ads "Account created" conversion; same reasoning as the
      // Windows waitlist event).
      pushDataLayerEvent("mac_link_requested", {
        source: "mac_link",
        placement,
      });
    } catch {
      /* analytics must never break the form */
    }
    setDone(true);
    fireBlueConfetti();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setDone(false);
          setEmail("");
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          style={{
            ["--gamey-shadow" as string]: "#bcdcf2",
            ["--gamey-shadow-hover" as string]: "#9cc8ea",
            ...style,
          }}
          className={className}
        >
          <Mail className={iconClassName} />
          {label}
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md rounded-3xl">
        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl grid place-items-center text-white bg-gradient-to-br from-sky-500 to-blue-600">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">Sent!</DialogTitle>
              <DialogDescription className="text-center">
                Open it on your Mac and hit the big blue button. Vovy installs in seconds.
              </DialogDescription>
            </DialogHeader>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Get Vovy on your Mac</DialogTitle>
              <DialogDescription>
                Vovy is a Mac app. Drop your email and we'll send you the
                download link to open on your computer.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-2 space-y-3">
              <input
                type="email"
                required
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-sky-400"
              />
              <button
                type="submit"
                disabled={submitting}
                style={{
                  ["--gamey-shadow" as string]: "#0369a1",
                  ["--gamey-shadow-hover" as string]: "#075985",
                }}
                className="btn-gamey w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#00A3FF] px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#00A3FF]/90 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send me the link
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
